/**
 * Tela «Revisar atributos» (#216, spec §6.5): fila ÚNICA por produto com
 * alergénios + dietas + picância + tags — o dono confirma um prato por vez
 * em ~5 s. Substitui a ideia de filas separadas: um fluxo, dois PUTs
 * (allergens + attributes) em sequência por produto.
 */
import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  products as prodApi,
  categories as catApi,
  config as configApi,
  type Category,
  type Product,
} from '../lib/api.js'
import { Button, Empty, Spinner, Badge, toast } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'
import { currencyFor, displayTranslation } from './menu/shared.js'
import { intlLocaleFor } from '@self-restaurant/shared'
import {
  initialSelection as initialAllergens,
  toggleCode,
  chipSource,
  suggestionOf,
} from '../lib/allergens.js'
import { initialAttributes, pendingAttributesReview } from '../lib/attributes.js'
import type { AttributeSelection } from '../components/AttributeFields.js'
import { AllergenChips, AllergenLegend } from '../components/AllergenChips.js'
import { AttributeFields } from '../components/AttributeFields.js'

export function ReviewAttributes() {
  const qc = useQueryClient()
  const { t, lang } = useI18n()

  const { data: cfg } = useQuery({ queryKey: ['config'], queryFn: () => configApi.get() })
  // #419/#413: moeda do PAÍS do tenant; fallback pelo idioma base
  const primaryLocale = cfg?.primary_locale ?? 'pt'
  const currency = cfg?.currency ?? currencyFor(primaryLocale)
  // #428: locale de formatação Intl (língua base + país do restaurante)
  const priceLocale = intlLocaleFor(primaryLocale, cfg?.country_code)

  const { data: cats = [] } = useQuery({ queryKey: ['categories'], queryFn: () => catApi.list() })
  const { data: prods = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => prodApi.list(),
  })

  // fila: produtos ATIVOS com sugestões pendentes (atributos OU alergénios),
  // na ordem do catálogo; acompanhamos o id do produto atual (a fila encolhe
  // quando as confirmações invalidam a query)
  const queue = prods.filter((p) => p.is_active && pendingAttributesReview([p]).includes(p.id))
  const [currentId, setCurrentId] = useState<string | null>(null)
  const current = queue.find((p) => p.id === currentId) ?? queue[0]
  const done = prods.filter((p) => p.is_active).length - queue.length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  if (!current) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.attributes_review.title')}</h1>
        <Empty
          title={t('admin.attributes_review.empty_title')}
          description={t('admin.attributes_review.empty_desc')}
          action={
            <Link to="../menu">
              <Button size="sm" data-testid="review-attributes-open-menu">
                {t('admin.attributes_review.open_menu')}
              </Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="review-attributes-title">
          {t('admin.attributes_review.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('admin.attributes_review.subtitle')}</p>
      </div>
      <div className="flex items-center gap-2" data-testid="review-attributes-progress">
        <Badge color="indigo" className="text-xs">
          {t('admin.attributes_review.progress', {
            done: String(Math.max(0, done)),
            total: String(Math.max(0, done) + queue.length),
          })}
        </Badge>
      </div>

      <ReviewCard
        key={current.id}
        product={current}
        categories={cats}
        lang={lang}
        primaryLocale={primaryLocale}
        currency={currency}
        priceLocale={priceLocale}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['products'] })
          setCurrentId(null)
        }}
      />

      {/* próximo da fila, 1 clique para pular sem confirmar */}
      {queue.length > 1 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            data-testid="review-attributes-skip"
            onClick={() => {
              const idx = queue.findIndex((p) => p.id === current.id)
              setCurrentId(queue[(idx + 1) % queue.length]!.id)
            }}
          >
            {t('admin.attributes_review.skip')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Um produto da fila: alergénios + atributos num só confirmar. */
function ReviewCard({
  product,
  categories,
  lang,
  primaryLocale,
  currency,
  priceLocale,
  onDone,
}: {
  product: Product
  categories: Category[]
  lang: string
  primaryLocale: string
  currency: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale: string
  onDone: () => void
}) {
  const { t } = useI18n()
  const [codes, setCodes] = useState<number[]>(() => initialAllergens(product))
  const [attrs, setAttrs] = useState<AttributeSelection>(() => initialAttributes(product))

  const name = displayTranslation(product.translations, lang, primaryLocale)?.name ?? product.id
  const catName =
    displayTranslation(
      categories.find((c) => c.id === product.category_id)?.translations ?? [],
      lang,
      primaryLocale,
    )?.name ?? '—'

  const save = useMutation({
    mutationFn: async () => {
      // os dois PUTs do fluxo único (#216): alergénios e atributos
      await prodApi.confirmAllergens(product.id, codes)
      await prodApi.confirmAttributes(product.id, attrs)
    },
    onSuccess: () => {
      toast.success(t('admin.menu.allergens_saved'))
      onDone()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4"
      data-testid={`review-attributes-card-${product.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-base font-semibold text-gray-900" data-testid="review-attributes-name">
            {name}
          </p>
          <p className="text-xs text-gray-500">{catName}</p>
        </div>
        <p className="text-sm font-bold text-indigo-700">
          {(product.price_cents / 100).toLocaleString(priceLocale, { style: 'currency', currency })}
        </p>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">{t('admin.menu.allergens')}</p>
        <AllergenChips
          selected={codes}
          sourceOf={(code) => chipSource(suggestionOf(product, code)?.source)}
          onToggle={(code) => setCodes((s) => toggleCode(s, code))}
          testIdPrefix={`review-allergen-${product.id}-`}
        />
        <div className="mt-1">
          <AllergenLegend />
        </div>
      </div>

      <AttributeFields
        selection={attrs}
        onChange={setAttrs}
        testIdPrefix={`review-attr-${product.id}-`}
      />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
        <Button
          data-testid={`review-attributes-confirm-${product.id}`}
          loading={save.isPending}
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {t('admin.common.confirm')}
        </Button>
      </div>
      <p className="text-[11px] text-gray-400">{t('admin.import.review_confirm_note')}</p>
    </div>
  )
}
